import { useRef, useState } from "react";
import { FileUp, Loader2, Paperclip, Trash2 } from "lucide-react";

import { QuestionPromptCard, type QuestionPictureInfo } from "@/components/QuestionPrompt";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ApiError,
  deleteAnswerFile,
  uploadAnswerFile,
  type FileUploadRules,
  type SavedFileInfo,
} from "@/lib/api";
import {
  acceptAttribute,
  checkFileAgainstRules,
  describeAllowed,
  formatBytes,
  limitMb,
} from "@/lib/fileUploadRules";

interface FileUploadAnswerProps {
  promptMd: string;
  /** EP-19: the picture under the prompt, if the question has one. */
  picture?: QuestionPictureInfo | null;
  // The frozen rules (types, size, whether leaving the window is allowed);
  // null only for a malformed snapshot — the picker is then disabled.
  rules: FileUploadRules | null;
  saved: SavedFileInfo | null;
  token: string;
  attemptId: string;
  position: number;
  // Called with the server's own answer after an upload (the file) or a
  // removal (null) — the page mirrors it into its cache.
  onSaved: (file: SavedFileInfo | null) => void;
  // EF-11: the anti-cheat controller's picker window — the OS dialog blurs
  // the page, which must not count as a tab switch. Returns the end function.
  beginFilePicker?: (position: number) => () => void;
  // The recruiter's "preview as candidate": the prompt and the hint, no
  // upload (there is no attempt to attach to).
  readOnly?: boolean;
  // A forward-only test (no back navigation): the server finalises the answer
  // on the first upload and 409s a replace or a removal, so neither is offered
  // once a file is saved — and the candidate is told before choosing one.
  finalOnceUploaded?: boolean;
  // FEAT-25: false when the test runs with fullscreen AND activity monitoring
  // off — leaving the window is then not tracked at all, so the "not counted
  // against you" note would describe a rule that does not exist.
  showLeaveNote?: boolean;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = err.detail as
      | { code?: string; message?: string }
      | string
      | undefined;
    const code = detail && typeof detail === "object" ? detail.code : undefined;
    const message =
      detail && typeof detail === "object"
        ? detail.message
        : typeof detail === "string"
          ? detail
          : undefined;
    if (code === "FILE_STORAGE_UNAVAILABLE") {
      return "Uploads are temporarily unavailable — try again in a moment.";
    }
    if (
      code === "FILE_TOO_LARGE" ||
      code === "FILE_TYPE_NOT_ALLOWED" ||
      code === "FILE_CONTENT_MISMATCH" ||
      code === "FILE_EMPTY"
    ) {
      return message ?? "This file can't be uploaded.";
    }
    if (err.status === 409) return "This answer can no longer be changed.";
    if (err.status === 404) return "This question does not accept a file right now.";
    // 2026-10-15 (Emilia, Sheet #13 round 2): every other status read the
    // same generic line, so a 500, the 20/min limiter and a dead attempt were
    // indistinguishable from the browser failing to read the file. Name the
    // status — the candidate's report-a-problem then carries it. Placeholder
    // wording (Emilia's call).
    if (err.status === 429) {
      return "Too many upload attempts — wait a minute and try again.";
    }
    if (err.status >= 500) {
      return `Upload failed (server error ${err.status}). Try again in a minute.`;
    }
    return `Upload failed (error ${err.status}). Reload the page and try again.`;
  }
  // No response at all: `fetch` rejects with a TypeError when the browser
  // could not READ the file it was handed (a OneDrive cloud-only placeholder,
  // a file another program holds locked, a cut connection) — the Network tab
  // shows "(failed)". Not our server, and "try again" would never fix it.
  if (err instanceof TypeError) {
    return "The browser could not read this file. Save it on this computer (not OneDrive) and try again.";
  }
  return "Upload failed. Please try again.";
}

// EF-11 — the candidate-facing file-upload question: the prompt, one file.
// The upload IS the save (its own route); nothing is graded in the browser
// or by the server — a recruiter downloads and scores it by hand. No
// progress bar: the repo has no XHR path, and the caps are single-digit MB.
export function FileUploadAnswer({
  promptMd,
  picture,
  rules,
  saved,
  token,
  attemptId,
  position,
  onSaved,
  beginFilePicker,
  readOnly = false,
  finalOnceUploaded = false,
  showLeaveNote = true,
}: FileUploadAnswerProps) {
  const locked = finalOnceUploaded && saved !== null;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const endPickerRef = useRef<(() => void) | null>(null);
  const [busy, setBusy] = useState<"upload" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputId = `file-answer-${position}`;

  const endPicker = (): void => {
    endPickerRef.current?.();
    endPickerRef.current = null;
  };

  const openPicker = (): void => {
    if (readOnly || busy !== null || !rules) return;
    endPickerRef.current = beginFilePicker?.(position) ?? null;
    inputRef.current?.click();
  };

  // Sheet #13 (2026-10-06): the <input> keeps the last pick until its value is
  // cleared, and Chromium fires no `change` when the SAME path is chosen
  // again — so a pick refused here (wrong type, over the limit, 0 bytes) left
  // the candidate choosing the fixed file over and over with nothing happening
  // (Emilia: three picks of a .docx "with 2 words", no upload, no message).
  // Cleared on EVERY exit, not only after an upload.
  const resetPicker = (): void => {
    if (inputRef.current) inputRef.current.value = "";
  };

  const onFileChosen = async (file: File | undefined): Promise<void> => {
    endPicker();
    try {
      if (!file || !rules) return;
      const check = checkFileAgainstRules(file, rules);
      if (!check.ok) {
        setError(check.message);
        return;
      }
      setError(null);
      setBusy("upload");
      try {
        const next = await uploadAnswerFile(token, position, attemptId, file);
        onSaved(next);
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBusy(null);
      }
    } finally {
      resetPicker();
    }
  };

  // A file dropped on the answer card goes through the SAME path as the
  // button (type, size, empty check, then the upload). The anti-cheat
  // controller leaves a file drop inside `[data-file-drop]` alone — until
  // 2026-10-15 it swallowed it as a "paste" and the file never arrived
  // (FEAT-29 side effect). Text drops are not ours: the controller records
  // those as before.
  const dropEnabled = !readOnly && busy === null && rules !== null && !locked;
  const onDragOver = (e: React.DragEvent): void => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = dropEnabled ? "copy" : "none";
  };
  const onDrop = (e: React.DragEvent): void => {
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    // Never let the browser navigate to the dropped file, even when the
    // question is read-only or locked.
    e.preventDefault();
    if (!dropEnabled) return;
    void onFileChosen(file);
  };

  const onRemove = async (): Promise<void> => {
    setError(null);
    setBusy("remove");
    try {
      await deleteAnswerFile(token, position, attemptId);
      onSaved(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <QuestionPromptCard promptMd={promptMd} picture={picture} />

      {rules?.relax_anticheat && showLeaveNote && (
        <div
          role="note"
          className="rounded-md border border-info/40 bg-info/5 px-3 py-2 text-sm text-muted-foreground"
        >
          You can leave this window to work on your file — that is not
          counted against you. Come back here to attach it.
        </div>
      )}

      <Card data-file-drop="" onDragOver={onDragOver} onDrop={onDrop}>
        <CardHeader>
          <CardTitle>Your answer</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {rules ? (
            <>
              <input
                ref={inputRef}
                id={inputId}
                type="file"
                accept={acceptAttribute(rules.allowed_extensions)}
                className="sr-only"
                disabled={readOnly || busy !== null}
                onChange={(e) => void onFileChosen(e.target.files?.[0])}
              />
              {saved && (
                <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  <Paperclip
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span
                    className="min-w-0 flex-1 truncate font-medium"
                    title={saved.filename}
                  >
                    {saved.filename}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatBytes(saved.size)}
                  </span>
                  {!readOnly && !locked && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="cursor-pointer"
                      onClick={() => void onRemove()}
                      disabled={busy !== null}
                    >
                      {busy === "remove" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      Remove
                    </Button>
                  )}
                </div>
              )}
              {locked ? (
                <p className="text-sm text-muted-foreground">
                  Your file is submitted. This test doesn't allow changing an
                  answer once it's in.
                </p>
              ) : (
              <Button
                type="button"
                variant={saved ? "outline" : "default"}
                className="cursor-pointer"
                onClick={openPicker}
                disabled={readOnly || busy !== null}
              >
                {busy === "upload" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <FileUp className="h-4 w-4" />
                    {saved ? "Replace file" : "Choose file"}
                  </>
                )}
              </Button>
              )}
              <p className="text-xs text-muted-foreground">
                Allowed: {describeAllowed(rules.allowed_extensions)} · up to{" "}
                {limitMb(rules)} MB · one file. A recruiter downloads and scores
                it by hand.
                {finalOnceUploaded && !saved && !readOnly
                  ? " Your first upload is final on this test — check the file before you choose it."
                  : null}
              </p>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              This question does not accept a file right now.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FileUploadAnswer;
